import { toast } from "react-hot-toast";
import { apiConnector } from "../apiconnector";
import { courseEndpoints } from "../apis";
import { setCategories } from "../../redux/slices/courseSlice";
import { base64ImageBlob, base64VideoBlob } from "../../utils/base64Converter";
import { setReviewModal, setViewCourseLoading } from "../../redux/slices/viewCourseSlice";
import updateUserLocalStorage from "../../utils/updateUserLocalStorage";
import { setBlurLoading } from "../../redux/slices/authSlice";

const {
    GET_CATEGORIES_API,
    GET_AVERAGE_RATING_REVIEW_API,
    CREATE_CATEGORY_API,
    CREATE_COURSE_API,
    CREATE_RATING_REVIEW_API,
    CREATE_SECTION_API,
    CREATE_SUB_SECTION_API,
    UPDATE_RATING_REVIEW_API,
    UPDATE_SECTION_API,
    UPDATE_SUB_SECTION_API,
    DELETE_SECTION_API,
    DELETE_SUB_SECTION_API,
    GET_ALL_RATING_REVIEW_API,
    GET_ALL_COURSES_API,
    GET_CATEGORY_PAGE_DETAILS_API,
    GET_SPECIFIC_COURSE_API,
    GET_COMPLETE_COURSE_API,
    GET_COURSE_FEES_COURSES_API,
    GET_ALL_INSTRUCTORE_COURSES_API,
    DELETE_COURSE_API,
    UPDATE_COURSE_PROGRESS_API,
} = courseEndpoints;

export const getAllCategories = (setSubLinks) => {
    return async (dispatch) => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector("GET", GET_CATEGORIES_API);
            if (!response.data.success) {
                return;
            }
            const allCategories = response.data.allCategories;
            setSubLinks(allCategories ?? []);
            dispatch(setCategories(allCategories));
        } catch (error) {
            console.error("Error in fetching categories list - ", error.message);
        } finally {
            dispatch(setBlurLoading(false));
        }
    };
};

export const getAverageRatingReview = (setAvgRating, courseId) => {
    return async () => {
        try {
            const response = await apiConnector("POST", GET_AVERAGE_RATING_REVIEW_API, { courseId });
            setAvgRating(response.data.averageRating);
        } catch (error) {
            console.error("Error in average rating API - ", error.message);
        }
    };
};

export const createCategory = (name, description) => {
    return async () => {
        try {
            const response = await apiConnector("POST", CREATE_CATEGORY_API, { name, description });
            if (!response.data.success) {
                toast.error("Category creation failed.", { duration: 3000 });
            } else {
                toast.success("Category created successfully.", { duration: 3000 });
            }
        } catch (error) {
            console.error("Error in creating category - ", error.message);
        }
    };
};

export const createCourse = async (courseInformation, publishOrDraft) => {
    try {
        const blob = base64ImageBlob(courseInformation.courseImage);
        const formDataToSend = new FormData();
        formDataToSend.append("courseName", courseInformation.courseTitle);
        formDataToSend.append("courseDescription", courseInformation.courseShortDesc);
        formDataToSend.append("courseLanguage", courseInformation.courseLanguage);
        formDataToSend.append("whatYouWillLearn", courseInformation.courseBenefits);
        formDataToSend.append("price", courseInformation.coursePrice);
        formDataToSend.append("category", courseInformation.courseCategory);
        formDataToSend.append("tag", courseInformation.courseTags);
        formDataToSend.append('instructions', courseInformation.courseRequirements);
        formDataToSend.append('status', publishOrDraft);
        formDataToSend.append('thumbnailImage', blob);

        const response = await apiConnector("POST", CREATE_COURSE_API, formDataToSend);
        if (!response.data.success) {
            toast.error("Course creation failed, please try again or contact us for assistance.", { duration: 3000 });
        } else {
            return response;
        }
    } catch (error) {
        console.error("Error occurred while creating the course - ", error.message);
    }
};

export const createSection = async (sectionName, courseID) => {
    try {
        const response = await apiConnector("POST", CREATE_SECTION_API, { sectionName, courseID });
        if (!response.data.success) {
            toast.error("Section creation failed. Please try again or contact support for assistance.", { duration: 3000 });
        } else {
            return response;
        }
    } catch (error) {
        console.error("Error occurred while creating the section - ", error.message);
    }
};

export const createSubSection = async (sectionID, element, courseID) => {
    const blob = base64VideoBlob(element.file);
    const formDataToSendInSubSection = new FormData();
    formDataToSendInSubSection.append('title', element.title);
    formDataToSendInSubSection.append('sectionID', `${sectionID}`);
    formDataToSendInSubSection.append('description', element.description);
    formDataToSendInSubSection.append('timeDuration', element.duration);
    formDataToSendInSubSection.append('courseID', courseID);
    formDataToSendInSubSection.append('videoFile', blob);
    try {
        const response = await apiConnector("POST", CREATE_SUB_SECTION_API, formDataToSendInSubSection);
        if (!response.data.success) {
            toast.error("Sub-section creation failed. Please try again or contact support for assistance.", { duration: 3000 });
        } else {
            return response;
        }
    } catch (error) {
        console.error("Error occurred while creating the sub-section - ", error.message);
    }
};

export const createRatingReview = (rating, review, courseId, dispatch) => {
    return async () => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector("POST", CREATE_RATING_REVIEW_API, { rating, review, courseId });
            if (!response.data.success) {
                toast.error("Rating and review creation failed.", { duration: 3000 });
            } else {
                toast.success("Review posted successfully.", { duration: 3000 });
                await updateUserLocalStorage(dispatch);
            }
        } catch (error) {
            console.error("Error in creating rating and review - ", error.message);
        } finally {
            dispatch(setReviewModal(false));
            dispatch(setBlurLoading(false));
        }
    };
};

export const updateRatingAndReview = (rating, review, courseId, reviewId, dispatch) => {
    return async () => {
        dispatch(setBlurLoading(true));
        try {
            const response = await apiConnector("POST", UPDATE_RATING_REVIEW_API, { rating, review, courseId, reviewId });
            if (!response.data.success) {
                toast.error("Review updation failed.", { duration: 3000 });
            } else {
                toast.success("Review Updated");
                await updateUserLocalStorage(dispatch);
            }
        } catch (error) {
            console.error("Error while updating review - ", error.message);
        } finally {
            dispatch(setBlurLoading(false));
            dispatch(setReviewModal(false));
        }
    };
};

export const updateSection = (sectionID, newSectionName, dispatch) => {
    return async () => {
        dispatch(setViewCourseLoading(true));
        try {
            const response = await apiConnector("POST", UPDATE_SECTION_API, { sectionID, newSectionName });
            if (!response.data.success) {
                toast.error("Section update failed.", { duration: 3000 });
            } else {
                toast.success("Section updated successfully.", { duration: 3000 });
                await updateUserLocalStorage(dispatch);
            }
        } catch (error) {
            console.log("Error in updateSection API - ", error.message);
        } finally {
            dispatch(setViewCourseLoading(false));
        }
    };
};

export const updateSubSection = (subSectionID, newTitle, newDescription, newTimeDuration) => {
    return async () => {
        try {
            const response = await apiConnector("POST", UPDATE_SUB_SECTION_API, {
                subSectionID,
                newTitle,
                newDescription,
                newTimeDuration,
            });

            if (!response.data.success) {
                toast.error("Sub-section update failed.", { duration: 3000 });
            } else {
                toast.success("Sub-section updated successfully.", { duration: 3000 });
            }
        } catch (error) {
            console.log("Error in updateSubSection API - ", error.message);
        }
    };
};

export const deleteSection = (sectionID, courseID, dispatch) => {
    return async () => {
        dispatch(setViewCourseLoading(true));
        try {
            const response = await apiConnector("POST", DELETE_SECTION_API, { sectionID, courseID });
            if (!response.data.success) {
                toast.error("Section deletion failed.", { duration: 3000 });
            } else {
                toast.success("Section deleted successfully.", { duration: 3000 });
                await updateUserLocalStorage(dispatch);
            }
        } catch (error) {
            console.log("Error in deleteSection API - ", error.message);
        } finally {
            dispatch(setViewCourseLoading(false));
        }
    };
};

export const deleteSubSection = (sectionID, subSectionID, dispatch) => {
    return async () => {
        dispatch(setViewCourseLoading(true));
        try {
            const response = await apiConnector("POST", DELETE_SUB_SECTION_API, { sectionID, subSectionID });
            if (!response.data.success) {
                toast.error("Sub-section deletion failed.", { duration: 3000 });
            } else {
                toast.success("Sub-section deleted successfully.", { duration: 3000 });
                await updateUserLocalStorage(dispatch);
            }
        } catch (error) {
            console.log("Error in deleteSubSection API - ", error.message);
        } finally {
            dispatch(setViewCourseLoading(false));
        }
    };
};

export const getAllRatingReviews = (setRatings) => {
    return async () => {
        try {
            const response = await apiConnector("GET", GET_ALL_RATING_REVIEW_API);
            if (!response.data.success) {
                toast.error("Fetching rating and reviews failed.", { duration: 3000 });
            } else {
                setRatings(response.data.data);
            }
        } catch (error) {
            console.log("Error in getAllRatingReviews API - ", error.message);
        }
    };
};

export const getAllCourses = () => {
    return async () => {
        try {
            const response = await apiConnector("GET", GET_ALL_COURSES_API);
            if (!response.data.success) {
                toast.error("Fetching courses failed.", { duration: 3000 });
            } else {
                // Note: The specific processing of the fetched data is not shown in this code snippet
            }
        } catch (error) {
            console.log("Error in getAllCourses API - ", error.message);
        }
    };
};

export const getCategoryPageDetails = async (categoryName) => {
    try {
        const getCategoryResponse = await apiConnector("GET", courseEndpoints.GET_CATEGORIES_API);
        const categoryId = getCategoryResponse?.data?.allCategories?.find(
            (cat) => cat.name.toLowerCase() === categoryName.toLowerCase()
        )?._id;

        if (!categoryId) {
            console.log('Category ID not found');
            return;
        }
        const response = await apiConnector("POST", GET_CATEGORY_PAGE_DETAILS_API, { categoryId });
        if (!response.data.success) {
            return;
        }
        return response.data.data;
    } catch (error) {
        console.log('Error in category page API - ', error.message);
    }
};

export const getCourse = async (courseId) => {
    try {
        const response = await apiConnector("POST", GET_SPECIFIC_COURSE_API, { courseId });
        if (!response.data.success) {
            toast.error("Fetching course details failed.", { duration: 3000 });
        } else {
            return response.data.data;
        }
    } catch (error) {
        console.log("Error in getSpecificCourse API - ", error.message);
    }
};

export const getCompleteCourse = async (courseId) => {
    try {
        const response = await apiConnector("POST", GET_COMPLETE_COURSE_API, { courseId });
        if (!response.data.success) {
            toast.error("Fetching complete course details failed.", { duration: 3000 });
        } else {
            return response.data.data;
        }
    } catch (error) {
        console.log("Error in getCompleteCourse API - ", error.message);
    }
};

export const getInstructorCourses = async (instructorUserId) => {
    try {
        const response = await apiConnector("POST", GET_ALL_INSTRUCTORE_COURSES_API, { instructorUserId });
        if (!response.data.success) {
            return toast.error('Courses Details Fetching Failed. Please try again or contact us.');
        }
        return response;
    } catch (error) {
        console.log('Course Details fetching failed. Error in courseApi - ', error.message);
    }
};

export const deleteCourse = async (courseID) => {
    try {
        const response = await apiConnector("DELETE", DELETE_COURSE_API, { courseID });
        if (!response.data.success) {
            return toast.error('Course Deletion Failed');
        }
    } catch (error) {
        console.log('Course deletion failed due to - ', error.message);
    }
};

export const updateCourseProgress = (courseId, subSectionId) => {
    return async (dispatch) => {
        dispatch(setViewCourseLoading(true));
        try {
            await apiConnector('POST', UPDATE_COURSE_PROGRESS_API, { courseId, subSectionId });
            updateUserLocalStorage(dispatch);
        } catch (error) {
            console.log('Course update course progress failed due to - ', error.message);
        } finally {
            dispatch(setViewCourseLoading(false));
        }
    };
};

export const getTeachingAnalyticsData = (instructorUserId, setTeachingAnalyticsData, setLoading) => {
    return async () => {
        setLoading(true);
        try {
            const response = await apiConnector("POST", GET_ALL_INSTRUCTORE_COURSES_API, { instructorUserId });
            if (!response.data.success) {
                return toast.error('Courses Details Fetching Failed. Try Again or Contact Us');
            }
            const teachingAnalyticsData = response.data.courses.map((course) => {
                return {
                    courseName: course.courseName,
                    studentCount: course.studentsEnrolled.length,
                    earn: course.studentsEnrolled.length * Number(course.price),
                    price: Number(course.price),
                };
            });
            setTeachingAnalyticsData(teachingAnalyticsData);
        } catch (error) {
            console.log('Course Details fetching failed. Error in courseApi - ', error.message);
        } finally {
            setLoading(false);
        }
    };
};

export const getCourseFeedCourses = (setCourseFeed, setConfirmationModal, setLoading) => {
    return async () => {
        setLoading(true);
        try {
            const response = await apiConnector("GET", GET_COURSE_FEES_COURSES_API);
            if (!response.data.success) {
                setConfirmationModal({
                    text1: 'Course Feed Not Found',
                    text2: 'Course Feed data fetching failed due to network error. Reload or try again.',
                    closeModal: () => setConfirmationModal(null)
                });
            } else {
                setCourseFeed(response.data.data);
            }
        } catch (error) {
            console.log('Error while fetching course feed courses - ', error.message);
            setConfirmationModal({
                text1: 'Course Feed Not Found',
                text2: 'Internal server error relaod or try again.',
                closeModal: () => setConfirmationModal(null)
            });
        } finally {
            setLoading(false);
        }
    };
};